import { Grid, Box, Button, Alert, AlertTitle } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import React from "react";
import ListCard from "./ListCard/ListCard";
import GridLoadingState from "./GridLoadingState";
import ListLoadingState from "./ListLoadingState";
import { FIXED_HEADER_OFFSET_PX } from "../../components/layouts/headerLayout";
import { getFilterBandBackground } from "../../components/layouts/SubHeader/subHeaderBannerStyles";
import { isMockSearchCompaniesEnabled } from "../../fixture/mockSearchCompanies";
import HorizontalFilterBar from "./FilterBar/HorizontalFilterBar";
import V3SearchSubHeader from "./FilterBar/V3SearchSubHeader";
import EnrichmentGrid from "../Enrichment/EnrichmentGrid";
import CompanyResultsEmptyState, { SEARCH_EMPTY_ERROR, SEARCH_EMPTY_NO_MATCH } from "../../components/EmptyState/CompanyResultsEmptyState";
import SearchResultsPagination from "./SearchResultsPagination";
import MobileSearchFilterBar from "./mobile/MobileSearchFilterBar";
import MobileBulkActionBar from "./mobile/MobileBulkActionBar";

const FILTER_BAND_STICKY_SX = {
  position: "sticky",
  top: FIXED_HEADER_OFFSET_PX,
  zIndex: (theme) => theme.zIndex.appBar - 1,
  width: "100%",
  px: { xs: 2, md: "40px" },
  pt: { xs: 2, md: "32px" },
  pb: { xs: 2, md: "32px" },
  boxSizing: "border-box",
  background: (theme) => getFilterBandBackground(theme),
};

const CONTENT_BELOW_FILTER_BAND_SX = {
  padding: { xs: "0px 16px", md: "0px 40px 0px 40px" },
};

function CompanySearchResults({
  isMobile, filterSectionProps, resultView, setResultView, totalRows, hasSearchCompleted, openExportReview, isExporting, selectedIds, setOpenSaveSearchDialog, saveSearchDisabled, searchError, isRetryingSearch, loading, performSearch, hasNaturalQuery, navigate, showingSpreadsheet, showResultsEmptyState, showListCards, companies, selectedCompanies, handleSpreadsheetSelectionChange, spreadsheetDraftSessionId, setSpreadsheetDraftSessionHasData, activeSavedSearchId, isSavedSearchDirty, page, rowsPerPage, pageStartCursorsRef, handlePageChange, handleRowsPerPageChange, setSelectedIds
}) {
  return (<>
      <V3SearchSubHeader />
      <Box sx={FILTER_BAND_STICKY_SX} data-testid="search-filter-band">
        {isMobile ? (
          <MobileSearchFilterBar
            {...filterSectionProps}
            resultView={resultView}
            onResultViewChange={setResultView}
            totalRows={totalRows}
            showResultsCount={hasSearchCompleted}
            onExport={openExportReview}
            isExporting={isExporting}
            exportTitle={
              selectedIds.size > 0
                ? `Download ${selectedIds.size} selected companies (.csv)`
                : "Download up to 1k results (.csv)"
            }
            onSaveSearch={() => setOpenSaveSearchDialog(true)}
            saveSearchDisabled={saveSearchDisabled}
          />
        ) : (
          <HorizontalFilterBar
            {...filterSectionProps}
            resultView={resultView}
            onResultViewChange={setResultView}
            totalRows={totalRows}
            showResultsCount={hasSearchCompleted}
            onExport={openExportReview}
            isExporting={isExporting}
            exportTitle={
              selectedIds.size > 0
                ? `Download ${selectedIds.size} selected companies (.csv)`
                : "Download up to 1k results (.csv)"
            }
            onSaveSearch={() => setOpenSaveSearchDialog(true)}
            saveSearchDisabled={saveSearchDisabled}
          />
        )}
      </Box>
      <Grid container sx={CONTENT_BELOW_FILTER_BAND_SX}>

        <Grid item xs={12} md={12} lg={12}>

            <Box>
              {searchError === 'timeout' && !loading && (
                <Alert
                  severity="info"
                  icon={<TuneIcon />}
                  sx={{ mb: 3, borderRadius: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => performSearch(0, { resetCursors: true })}>
                      Retry
                    </Button>
                  }
                >
                  <AlertTitle>Search timed out</AlertTitle>
                  {hasNaturalQuery
                    ? 'Your query may be too broad. Try being more specific — for example, instead of "HVAC companies" try "commercial HVAC contractors in Texas with 50+ employees".'
                    : 'Try narrowing your search by adding more filters such as location, industry, or employee/revenue ranges.'}
                </Alert>
              )}
              {searchError === 'subscription' && !loading && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3, borderRadius: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => navigate('/settings/subscription')}>
                      Upgrade
                    </Button>
                  }
                >
                  <AlertTitle>Subscription required</AlertTitle>
                  An active subscription is needed to search. Please upgrade your plan to continue.
                </Alert>
              )}
              {searchError === 'generic' && !loading && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3, borderRadius: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => performSearch(0, { resetCursors: true })}>
                      Retry
                    </Button>
                  }
                >
                  <AlertTitle>Search failed</AlertTitle>
                  Something went wrong. Please try again or adjust your filters.
                </Alert>
              )}
              {loading && isRetryingSearch && (
                <Alert severity="info" role="status" sx={{ mb: 3, borderRadius: 2 }}>
                  Still searching — this is taking longer than usual.
                </Alert>
              )}
              {loading ? (
                showingSpreadsheet ? (
                  <GridLoadingState />
                ) : (
                  <ListLoadingState />
                )
              ) : showResultsEmptyState ? (
                <CompanyResultsEmptyState variant={searchError ? SEARCH_EMPTY_ERROR : SEARCH_EMPTY_NO_MATCH} />
              ) : (
                <>
                  {showListCards && companies?.map((c) => (
                    <ListCard key={c.stagedId} company={c} compact={true} mobile={isMobile} />
                  ))}
                  {showingSpreadsheet && (
                    isMobile ? (
                      <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                        <EnrichmentGrid
                          companies={companies} retainedCompanies={selectedCompanies}
                          selectedRows={selectedIds}
                          onSelectedRowsChange={handleSpreadsheetSelectionChange}
                          draftSessionId={spreadsheetDraftSessionId}
                          onDraftSessionUsed={() => setSpreadsheetDraftSessionHasData(true)}
                          searchEnrichmentContext={{workspaceId: activeSavedSearchId, isDirty: isSavedSearchDirty}}
                        />
                      </Box>
                    ) : (
                      <EnrichmentGrid
                        companies={companies} retainedCompanies={selectedCompanies}
                        selectedRows={selectedIds}
                        onSelectedRowsChange={handleSpreadsheetSelectionChange}
                        draftSessionId={spreadsheetDraftSessionId}
                        onDraftSessionUsed={() => setSpreadsheetDraftSessionHasData(true)}
                        searchEnrichmentContext={{workspaceId: activeSavedSearchId, isDirty: isSavedSearchDirty}}
                      />
                    )
                  )}
                  <SearchResultsPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    lastReachablePage={isMockSearchCompaniesEnabled()
                      ? Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1)
                      : pageStartCursorsRef.current.length - 1}
                    disabled={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      <MobileBulkActionBar
        selectedCount={selectedIds.size}
        onClear={() => setSelectedIds(new Set())}
        onExport={openExportReview}
        onSaveSearch={() => setOpenSaveSearchDialog(true)}
        isExporting={isExporting}
        saveDisabled={saveSearchDisabled}
      />
    </>);
}

export { CompanySearchResults, FILTER_BAND_STICKY_SX, CONTENT_BELOW_FILTER_BAND_SX };
